import React, { useState, useRef, useCallback } from 'react';
import {
   Card,
   CardContent,
   Typography,
   Avatar,
   Box,
   Chip,
   Button,
   CircularProgress,
   Popover,
   Drawer,
   IconButton,
   TextField,
   InputAdornment,
   useMediaQuery,
   useTheme,
} from '@mui/material';
import {
   PhotoAlbum as PhotoAlbumIcon,
   Lock as LockIcon,
   Public as PublicIcon,
   CalendarToday as CalendarTodayIcon,
   Photo as PhotoIcon,
   PhotoLibrary as PhotoLibraryIcon,
   CloudUpload as CloudUploadIcon,
   Share as ShareIcon,
   ContentCopy as ContentCopyIcon,
   Close as CloseIcon,
   AttachMoney as AttachMoneyIcon,
   OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import InlinePhotoScroller from '../feed/InlinePhotoScroller';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const QRCode = require('qrcode.react').QRCodeSVG;

export interface AlbumCreated {
   id: string;
   card_type: 'album_created';
   userid: string;
   albumId: string;
   albumTitle: string;
   albumDescription?: string;
   albumCoverUrl?: string;
   albumPhotoCount: number;
   albumType: 'private' | 'public' | 'paid';
   albumIsPublic: boolean;
   albumDate?: string;
   albumPhotos?: string[];
   creatorId: string;
   creatorName: string;
   creatorPhoto?: string;
   creatorEmail: string;
   createdAt: any;
   seen: boolean;
   /** Set when this card was created for an invitee who accepted (not the original album creator). */
   fromInvitationAccept?: boolean;
   invitationMemberRole?: string;
   /** How the user gained access when not via invitation (Firestore: accessGrantedVia). */
   accessGrantedVia?: 'public_link' | 'waitlist';
}

interface AlbumCreatedCardProps {
   albumCreated: AlbumCreated;
   onViewAlbum: (albumId: string) => void;
   onMarkAsSeen?: (cardId: string) => void;
   onUpload?: (albumId: string) => void;
   isUploading?: boolean;
}

const TEAL = '#1D9E75';
const GOLD = '#C9A84C';
const CARD_BG = '#111815';
const INNER_BG = '#1A2420';
const INNER_BORDER = '#253530';
const STAT_LABEL = '#546E7A';

const getPrivacyChipStyle = (albumType: string, isPublic: boolean) => {
   if (albumType === 'paid') {
      return {
         bg: 'rgba(139,92,246,0.12)',
         color: '#8B5CF6',
         border: '1px solid rgba(139,92,246,0.3)',
         icon: <AttachMoneyIcon sx={{ fontSize: 14 }} />,
         label: 'Paid',
      };
   }
   if (isPublic) {
      return {
         bg: 'rgba(201,168,76,0.12)',
         color: GOLD,
         border: '1px solid rgba(201,168,76,0.3)',
         icon: <PublicIcon sx={{ fontSize: 14 }} />,
         label: 'Public',
      };
   }
   return {
      bg: 'rgba(29,158,117,0.12)',
      color: TEAL,
      border: '1px solid rgba(29,158,117,0.3)',
      icon: <LockIcon sx={{ fontSize: 14 }} />,
      label: 'Private',
   };
};

const AlbumCreatedCard: React.FC<AlbumCreatedCardProps> = ({
   albumCreated,
   onViewAlbum,
   onMarkAsSeen,
   onUpload,
   isUploading,
}) => {
   const { currentUser } = useAuth();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   const [showShare, setShowShare] = useState(false);
   const [copied, setCopied] = useState(false);
   const shareAnchorRef = useRef<HTMLButtonElement | null>(null);

   const handleClick = useCallback(() => {
      if (onMarkAsSeen) {
         onMarkAsSeen(albumCreated.id);
      }
      onViewAlbum(albumCreated.albumId);
   }, [albumCreated.id, albumCreated.albumId, onMarkAsSeen, onViewAlbum]);

   const getTimeAgo = () => {
      if (!albumCreated.createdAt) return '';
      try {
         const date =
            albumCreated.createdAt.toDate?.() || new Date(albumCreated.createdAt.seconds * 1000);
         return formatDistanceToNow(date, { addSuffix: true });
      } catch {
         return '';
      }
   };

   const handleCopyLink = useCallback(async () => {
      const url = `${window.location.origin}/albums/public/${albumCreated.albumId}`;
      try {
         await navigator.clipboard.writeText(url);
         setCopied(true);
         setTimeout(() => setCopied(false), 2000);
      } catch {
         // fallback
      }
   }, [albumCreated.albumId]);

   const coverUrl = albumCreated.albumCoverUrl;
   const photoCount = albumCreated.albumPhotoCount;
   const isPublic = albumCreated.albumIsPublic;
   const albumType = albumCreated.albumType || (isPublic ? 'public' : 'private');
   const isOwner = currentUser?.uid === albumCreated.creatorId;
   const cardHeadline = (() => {
      if (albumCreated.accessGrantedVia === 'public_link') return 'Joined via public album';
      if (albumCreated.accessGrantedVia === 'waitlist') return 'Access approved';
      if (albumCreated.fromInvitationAccept) return 'Album shared with you';
      if (isOwner) return 'New Album Created!';
      return 'Album access';
   })();
   const privacyChip = getPrivacyChipStyle(albumType, isPublic);
   const shareUrl = `${window.location.origin}/albums/public/${albumCreated.albumId}`;

   const shareContent = (
      <Box sx={{ p: 2.5, width: isMobile ? '100%' : 320 }}>
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
               Share Album
            </Typography>
            <IconButton size="small" onClick={() => setShowShare(false)} sx={{ color: '#B0BEC5' }}>
               <CloseIcon fontSize="small" />
            </IconButton>
         </Box>

         <Typography variant="body2" sx={{ color: '#B0BEC5', mb: 0.5, fontSize: '0.75rem' }}>
            {albumCreated.albumTitle}
         </Typography>

         <Typography variant="caption" sx={{ color: STAT_LABEL, mb: 2, display: 'block' }}>
            Album Link
         </Typography>
         <TextField
            fullWidth
            size="small"
            value={shareUrl}
            InputProps={{
               readOnly: true,
               endAdornment: (
                  <InputAdornment position="end">
                     <IconButton size="small" onClick={handleCopyLink} sx={{ color: copied ? TEAL : '#B0BEC5' }}>
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                     </IconButton>
                  </InputAdornment>
               ),
               sx: {
                  fontSize: '0.8rem',
                  color: '#FFFFFF',
                  backgroundColor: INNER_BG,
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-notchedOutline': {
                     borderColor: INNER_BORDER,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                     borderColor: TEAL,
                  },
               },
            }}
            sx={{ mb: 1 }}
         />
         {copied && (
            <Typography variant="caption" sx={{ color: TEAL, fontWeight: 600 }}>
               Copied to clipboard!
            </Typography>
         )}

         {/* QR Code */}
         <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="caption" sx={{ color: STAT_LABEL, mb: 1, display: 'block' }}>
               QR Code
            </Typography>
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  p: 2,
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  border: `1px solid ${INNER_BORDER}`,
               }}
            >
               <QRCode
                  value={shareUrl}
                  size={isMobile ? 160 : 180}
                  level="H"
                  fgColor="#000000"
                  bgColor="#ffffff"
               />
            </Box>
            <Typography variant="caption" sx={{ color: STAT_LABEL, mt: 1, display: 'block', textAlign: 'center' }}>
               Scan to open album
            </Typography>
         </Box>

         <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            onClick={() => {
               setShowShare(false);
               onViewAlbum(albumCreated.albumId);
            }}
            sx={{
               mt: 2,
               color: TEAL,
               borderColor: TEAL,
               textTransform: 'none',
               borderRadius: '8px',
               fontWeight: 600,
               '&:hover': { borderColor: TEAL, backgroundColor: 'rgba(29,158,117,0.08)' },
            }}
         >
            View Full Sharing Settings
         </Button>
      </Box>
   );

   return (
      <>
         <Card
            sx={{
               mb: 1.5,
               background: CARD_BG,
               border: '1px solid #1F2E2A',
               borderTop: `4px solid ${TEAL}`,
               borderRadius: '12px',
               overflow: 'visible',
               transition: 'all 0.2s',
               cursor: 'pointer',
               '&:hover': {
                  boxShadow: '0 4px 16px rgba(29,158,117,0.12)',
                  transform: 'translateY(-1px)',
               },
            }}
            onClick={handleClick}
         >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
               <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Album Cover Photo */}
                  {coverUrl ? (
                     <Box
                        component="img"
                        src={coverUrl}
                        alt={albumCreated.albumTitle}
                        sx={{
                           width: 72,
                           height: 72,
                           objectFit: 'cover',
                           borderRadius: '12px',
                           flexShrink: 0,
                        }}
                     />
                  ) : (
                     <Avatar
                        sx={{
                           width: 72,
                           height: 72,
                           bgcolor: TEAL,
                           borderRadius: '12px',
                           flexShrink: 0,
                        }}
                     >
                        <PhotoAlbumIcon sx={{ fontSize: 36, color: 'white' }} />
                     </Avatar>
                  )}

                  {/* Main Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                           variant="caption"
                           sx={{
                              fontWeight: 700,
                              color: TEAL,
                              fontSize: '11px',
                              letterSpacing: 2,
                              textTransform: 'uppercase',
                           }}
                        >
                           {cardHeadline}
                        </Typography>
                        <Chip
                           icon={privacyChip.icon}
                           label={privacyChip.label}
                           size="small"
                           sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: privacyChip.bg,
                              color: privacyChip.color,
                              border: privacyChip.border,
                              '& .MuiChip-icon': {
                                 color: privacyChip.color,
                              },
                           }}
                        />
                     </Box>

                     <Typography
                        sx={{
                           fontWeight: 700,
                           fontSize: '16px',
                           color: '#FFFFFF',
                           mb: 0.5,
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           display: '-webkit-box',
                           WebkitLineClamp: 1,
                           WebkitBoxOrient: 'vertical',
                        }}
                     >
                        {albumCreated.albumTitle}
                     </Typography>

                     {albumCreated.albumDescription && (
                        <Typography
                           variant="body2"
                           sx={{
                              color: '#B0BEC5',
                              mb: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                           }}
                        >
                           {albumCreated.albumDescription}
                        </Typography>
                     )}

                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <PhotoIcon sx={{ fontSize: 16, color: TEAL }} />
                           <Typography variant="caption" sx={{ color: STAT_LABEL }}>
                              {photoCount} photo{photoCount !== 1 ? 's' : ''}
                           </Typography>
                        </Box>

                        {albumCreated.albumDate && (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarTodayIcon sx={{ fontSize: 16, color: TEAL }} />
                              <Typography variant="caption" sx={{ color: STAT_LABEL }}>
                                 {new Date(albumCreated.albumDate).toLocaleDateString()}
                              </Typography>
                           </Box>
                        )}

                        <Typography variant="caption" sx={{ color: STAT_LABEL, ml: 'auto' }}>
                           {getTimeAgo()}
                        </Typography>
                     </Box>
                  </Box>
               </Box>

               {/* Photo Preview Strip */}
               <Box onClick={(e) => e.stopPropagation()} sx={{ mt: 1 }}>
                  <InlinePhotoScroller
                     albumId={albumCreated.albumId}
                     albumTitle={albumCreated.albumTitle}
                     initialPhotoCount={photoCount}
                  />
               </Box>

               {/* Action Buttons (Owner Only) */}
               {isOwner && (
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        mt: 1.5,
                        width: '100%',
                     }}
                     onClick={(e) => e.stopPropagation()}
                  >
                     <Button
                        size="small"
                        startIcon={<PhotoLibraryIcon sx={{ fontSize: 16 }} />}
                        onClick={(e) => {
                           e.stopPropagation();
                           onViewAlbum(albumCreated.albumId);
                        }}
                        sx={{
                           flex: 1,
                           backgroundColor: TEAL,
                           color: '#FFFFFF',
                           borderRadius: '8px',
                           py: 1.25,
                           fontSize: '13px',
                           fontWeight: 700,
                           textTransform: 'none',
                           '&:hover': { backgroundColor: '#178a65' },
                        }}
                     >
                        View Photos
                     </Button>

                     <Button
                        size="small"
                        startIcon={
                           isUploading ? (
                              <CircularProgress size={14} color="inherit" />
                           ) : (
                              <CloudUploadIcon sx={{ fontSize: 16 }} />
                           )
                        }
                        disabled={isUploading}
                        onClick={(e) => {
                           e.stopPropagation();
                           onUpload?.(albumCreated.albumId);
                        }}
                        sx={{
                           flex: 1,
                           backgroundColor: INNER_BG,
                           color: '#FFFFFF',
                           border: `1px solid ${TEAL}`,
                           borderRadius: '8px',
                           py: 1.25,
                           fontSize: '13px',
                           fontWeight: 600,
                           textTransform: 'none',
                           '&:hover': { backgroundColor: '#1F2E28' },
                           '&.Mui-disabled': { backgroundColor: INNER_BG, color: STAT_LABEL, borderColor: INNER_BORDER },
                        }}
                     >
                        {isUploading ? 'Uploading...' : 'Upload'}
                     </Button>

                     <Button
                        ref={shareAnchorRef}
                        size="small"
                        startIcon={<ShareIcon sx={{ fontSize: 16 }} />}
                        onClick={(e) => {
                           e.stopPropagation();
                           setShowShare(true);
                        }}
                        sx={{
                           flex: 1,
                           backgroundColor: INNER_BG,
                           color: GOLD,
                           border: '1px solid rgba(201,168,76,0.4)',
                           borderRadius: '8px',
                           py: 1.25,
                           fontSize: '13px',
                           fontWeight: 600,
                           textTransform: 'none',
                           '&:hover': { backgroundColor: '#1F2E28' },
                        }}
                     >
                        Share
                     </Button>

                     <Button
                        size="small"
                        startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                        onClick={(e) => {
                           e.stopPropagation();
                           window.open(`/albums/public/${albumCreated.albumId}`, '_blank');
                        }}
                        sx={{
                           flex: 1,
                           backgroundColor: INNER_BG,
                           color: '#2EC4B6',
                           border: '1px solid rgba(46,196,182,0.4)',
                           borderRadius: '8px',
                           py: 1.25,
                           fontSize: '13px',
                           fontWeight: 600,
                           textTransform: 'none',
                           '&:hover': { backgroundColor: '#1F2E28' },
                        }}
                     >
                        Gallery
                     </Button>
                  </Box>
               )}
            </CardContent>
         </Card>

         {/* Share Popup */}
         {isMobile ? (
            <Drawer
               anchor="bottom"
               open={showShare}
               onClose={() => setShowShare(false)}
               PaperProps={{
                  sx: {
                     backgroundColor: CARD_BG,
                     borderTopLeftRadius: 16,
                     borderTopRightRadius: 16,
                     border: `1px solid ${INNER_BORDER}`,
                     borderBottom: 'none',
                  },
               }}
            >
               {shareContent}
            </Drawer>
         ) : (
            <Popover
               open={showShare}
               anchorEl={shareAnchorRef.current}
               onClose={() => setShowShare(false)}
               anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
               transformOrigin={{ vertical: 'top', horizontal: 'center' }}
               slotProps={{
                  paper: {
                     sx: {
                        backgroundColor: CARD_BG,
                        border: `1px solid ${INNER_BORDER}`,
                        borderRadius: '12px',
                        mt: 1,
                     },
                  },
               }}
            >
               {shareContent}
            </Popover>
         )}
      </>
   );
};

export default AlbumCreatedCard;
